/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { IAppVisibilityProvider } from "../interfaces";
import { Log } from "../logging/Log";

/**
 * Browser-based implementation of {@link IAppVisibilityProvider}.
 *
 * Listens to the `document.visibilitychange` event and notifies all
 * registered subscribers when the app transitions between foreground
 * and background states.
 *
 * Uses a lazy singleton pattern — call {@link getInstance} to obtain
 * the shared instance. The document listener is registered once and
 * lives for the entire application lifetime (SPA).
 */
export class AppVisibilityProvider implements IAppVisibilityProvider {
  private static readonly TAG: string = "AppVisibilityProvider";

  private static mInstance: AppVisibilityProvider | null = null;

  private readonly mSubscribers: Set<(isBackground: boolean) => void> =
    new Set();

  private constructor() {
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    document.addEventListener(
      "visibilitychange",
      this.handleVisibilityChange,
      false
    );
    Log.i(
      AppVisibilityProvider.TAG,
      "constructor() Registered visibilitychange listener"
    );
  }

  /**
   * Get the singleton instance. Creates it on first call.
   * @returns The shared AppVisibilityProvider instance.
   */
  public static getInstance(): AppVisibilityProvider {
    if (
      AppVisibilityProvider.mInstance === null ||
      AppVisibilityProvider.mInstance === undefined
    ) {
      AppVisibilityProvider.mInstance = new AppVisibilityProvider();
    }
    return AppVisibilityProvider.mInstance;
  }

  /**
   * Reset the singleton instance. Used ONLY in tests.
   */
  public static resetInstance(): void {
    if (
      AppVisibilityProvider.mInstance !== null &&
      AppVisibilityProvider.mInstance !== undefined
    ) {
      document.removeEventListener(
        "visibilitychange",
        AppVisibilityProvider.mInstance.handleVisibilityChange,
        false
      );
      AppVisibilityProvider.mInstance.mSubscribers.clear();
      AppVisibilityProvider.mInstance = null;
    }
  }

  private handleVisibilityChange(): void {
    const isBackground: boolean = document.hidden;
    Log.d(
      AppVisibilityProvider.TAG,
      `handleVisibilityChange() isBackground=${String(isBackground)}`
    );
    this.mSubscribers.forEach((callback) => {
      try {
        callback(isBackground);
      } catch (error: unknown) {
        Log.e(
          AppVisibilityProvider.TAG,
          "handleVisibilityChange() subscriber threw",
          error
        );
      }
    });
  }

  public isInBackground(): boolean {
    return document.hidden;
  }

  public subscribe(callback: (isBackground: boolean) => void): void {
    this.mSubscribers.add(callback);
    Log.d(
      AppVisibilityProvider.TAG,
      `subscribe() subscriber count=${String(this.mSubscribers.size)}`
    );
  }

  public unsubscribe(callback: (isBackground: boolean) => void): void {
    this.mSubscribers.delete(callback);
    Log.d(
      AppVisibilityProvider.TAG,
      `unsubscribe() subscriber count=${String(this.mSubscribers.size)}`
    );
  }
}
